/**
 * صفحة إنشاء حساب — للمقيم/المستفيد.
 * التدفق: التحقق من يقين → تعبئة البيانات + OTP → نفاذ (إجباري) → إنشاء الحساب.
 *
 * FIX:
 * - Removed dependency on mintContinue() because it was causing timeout/error on localhost.
 * - otpStart() now works with optional continue token (from URL only).
 * - Create step no longer blocks if continue token is missing.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { signupCheckUser, signupInitiateOtp } from "../../../services/authService";
import { verifyPublic } from "../../../services/yaqeenService";
import { signUpUserPartner } from "./userPartnerService";
import { RECAPTCHA_SITE_KEY } from "../../../config";
import { normalizeKsaPhone } from "../../../utils/phone";
import { resolveDobFromYaqeen, toYyyyMmDd } from "../../../utils/yaqeenDob";
import { inputBaseCls, inputReadOnlyCls, labelCls, helpCls } from "../../../utils/formStyles";
import NafathVerificationStep from "../../nafath/NafathVerificationStep";
import type { NafathVerificationResult } from "../../nafath/useNafathVerification";

// Password rule used by React before calling the API.
// Must match backend/Odoo rule: 8+ chars, lowercase, uppercase, number, special char.
const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

function getPasswordRules(password: string) {
  return {
    minLength: password.length >= 8,
    lower: /[a-z]/.test(password),
    upper: /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
}

function isPasswordRuleValid(password: string) {
  return PASSWORD_REGEX.test(password);
}

function validatePassword(password: string, confirmPassword: string): string | null {
  if (!isPasswordRuleValid(password)) {
    return "كلمة المرور غير مطابقة للشروط. يجب أن تحتوي على 8 أحرف أو أكثر، وحرف صغير، وحرف كبير، ورقم، وحرف خاص.";
  }

  if (password !== confirmPassword) {
    return "كلمة المرور وتأكيد كلمة المرور غير متطابقين.";
  }

  return null;
}

const onlyDigits = (s: string) => (s || "").replace(/\D+/g, "");
const isCitizenId = (nin: string) => /^\s*1/.test(onlyDigits(nin));
const isResidentId = (nin: string) => /^\s*[23]/.test(onlyDigits(nin));

const HIJRI_MONTHS: Array<[string, string]> = [
  ["01", "محرم"],
  ["02", "صفر"],
  ["03", "ربيع الأول"],
  ["04", "ربيع الثاني"],
  ["05", "جمادى الأولى"],
  ["06", "جمادى الآخرة"],
  ["07", "رجب"],
  ["08", "شعبان"],
  ["09", "رمضان"],
  ["10", "شوال"],
  ["11", "ذو القعدة"],
  ["12", "ذو الحجة"],
];

export default function ResidentOrBenefactorAccountCreation() {
  const navigate = useNavigate();
  const location = useLocation();

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const resendRecaptchaRef = useRef<ReCAPTCHA>(null);
  const yaqeenRecaptchaRef = useRef<ReCAPTCHA>(null);

  // Optional continue token and returnTo from URL
  const { continueTokenFromUrl, returnTo } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      continueTokenFromUrl: params.get("c") || params.get("continueToken") || "",
      returnTo: params.get("returnTo") || "",
    };
  }, [location.search]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);

  const [name, setName] = useState("");
  const [nationalId, setNationalId] = useState("");
  const [countryName, setCountryName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [dobHijri, setDobHijri] = useState("");
  const [userLang, setUserLang] = useState("ar");
  const [step, setStep] = useState<"yaqeen" | "form" | "nafath" | "creating">("yaqeen");

  const [hijriY, setHijriY] = useState("");
  const [hijriM, setHijriM] = useState("");
  const [gregDate, setGregDate] = useState("");

  const [yaqeenCaptcha, setYaqeenCaptcha] = useState<string | null>(null);
  const [yaqeenLoading, setYaqeenLoading] = useState(false);

  const [otpCode, setOtpCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [resendOtpLoading, setResendOtpLoading] = useState(false);

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [resendCaptchaToken, setResendCaptchaToken] = useState<string | null>(null);
  const [showResendSection, setShowResendSection] = useState(false);

  const [otpSent, setOtpSent] = useState(false);

  // New signup OTP challenge returned from /api/auth/signup/initiate-otp
  const [challengeId, setChallengeId] = useState("");

  const [resendSeconds, setResendSeconds] = useState(0);

  // OTP code we will use for final account creation (after Nafath)
  const [pendingOtpCode, setPendingOtpCode] = useState<string>("");

  const translateCheckUserMessage = (raw?: string | null) => {
    const msg = (raw || "").toString().trim();
    if (!msg) return null;
    const parts = msg.split(",").map((s) => s.trim()).filter(Boolean);
    const mapped = parts
      .map((p) => {
        if (p === "national_id_already_exists") return "رقم الهوية مسجل مسبقاً";
        if (p === "mobile_already_exists") return "رقم الجوال مسجل مسبقاً";
        if (p === "email_already_exists") return "البريد الإلكتروني مسجل مسبقاً";
        if (p === "user_available") return null;
        return null;
      })
      .filter(Boolean) as string[];
    if (mapped.length) return mapped.join("، ");
    if (msg.includes("already_exists") || msg.includes("already exists")) return "لديك حساب بالفعل. يرجى تسجيل الدخول.";
    return null;
  };

  const user = useMemo(
    () => ({
      name: name.trim(),
      nationalId: nationalId.trim(),

      // Login must be National ID / Resident ID
      login: nationalId.trim(),

      // New field required by .NET/Odoo signup API
      password: password.trim(),

      countryName: countryName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      dob: dob.trim(),
      userLang,
    }),
    [name, nationalId, password, countryName, email, phone, dob, userLang]
  );


  const passwordRules = useMemo(() => getPasswordRules(password), [password]);
  const isPasswordStrong = isPasswordRuleValid(password);
  const isPasswordMatch = !!confirmPassword && password === confirmPassword;
  const isPasswordValid = isPasswordStrong && isPasswordMatch;

  const passwordInputCls = `${inputBaseCls} ${
    passwordTouched || password
      ? isPasswordStrong
        ? "border-emerald-500 focus:border-emerald-500 focus:ring-emerald-500"
        : "border-red-500 focus:border-red-500 focus:ring-red-500"
      : ""
  }`;

  const confirmPasswordInputCls = `${inputBaseCls} ${
    confirmPasswordTouched || confirmPassword
      ? isPasswordMatch
        ? "border-emerald-500 focus:border-emerald-500 focus:ring-emerald-500"
        : "border-red-500 focus:border-red-500 focus:ring-red-500"
      : ""
  }`;

  const isFormValid =
    !!user.name &&
    !!user.nationalId &&
    !!user.login &&
    !!user.password &&
    !!user.email &&
    !!user.phone &&
    !!user.dob &&
    normalizeKsaPhone(user.phone).ok &&
    isPasswordValid;

  // Resend OTP can only be clicked after 60 seconds.
  useEffect(() => {
    if (resendSeconds <= 0) return;

    const timer = window.setInterval(() => {
      setResendSeconds((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendSeconds]);

  const hijriYears = useMemo(() => {
    const approx = new Date().getFullYear() - 579;
    return Array.from({ length: 150 }, (_, i) => String(approx - i));
  }, []);

  /* ------------------------------- STEP 1: Verify Yaqeen ------------------------------- */
  const handleVerifyYaqeen = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    const nin = nationalId.trim();
    if (!nin) {
      setError("يرجى إدخال رقم الهوية الوطنية.");
      return;
    }
    if (!yaqeenCaptcha) {
      setError("يرجى إكمال التحقق (reCAPTCHA).");
      return;
    }

    // First: check if user already exists (business response may be 400)
    try {
      const chk = await signupCheckUser({ nationalId: onlyDigits(nin) });
      const m = (chk?.message || "").toString();
      if (chk?.success === true && m.includes("user_available")) {
        // continue
      } else if (m.includes("already_exists")) {
        setError(translateCheckUserMessage(m) || "لديك حساب بالفعل. يرجى تسجيل الدخول.");
        return;
      }
    } catch (err: unknown) {
      // If checkuser fails unexpectedly (network/server), keep message but allow user retry.
      const raw = err instanceof Error ? err.message : String(err);
      setError(raw || "تعذر التحقق من توفر الحساب.");
      return;
    }

    const citizen = isCitizenId(nin);
    const resident = isResidentId(nin);

    let dateOfBirth = "";

    if (citizen) {
      if (!hijriY || !hijriM) {
        setError("يرجى اختيار سنة وشهر الميلاد (هجري).");
        return;
      }
      // citizen => yyyy-MM
      dateOfBirth = `${hijriY}-${hijriM}`;
    } else if (resident) {
      // resident => full date
      dateOfBirth = toYyyyMmDd(gregDate) || gregDate.trim();
      if (!dateOfBirth || dateOfBirth.length < 10) {
        setError("يرجى إدخال تاريخ الميلاد (ميلادي).");
        return;
      }
    } else {
      setError("رقم الهوية غير معروف (مواطن يبدأ بـ 1، مقيم بـ 2 أو 3).");
      return;
    }

    setYaqeenLoading(true);
    try {
      const res = await verifyPublic({ identificationId: nin, dateOfBirth });

      const { dob: resolvedDob, dobHijri: resolvedHijri } = resolveDobFromYaqeen(res, {
        dateOfBirthSent: dateOfBirth,
        isCitizen: citizen,
        gregDateInput: gregDate,
      });

      setName(res.nameAr ?? "");
      setNationalId(nin);
      setDob(resolvedDob);
      setDobHijri(resolvedHijri);
      setCountryName(res.countryName ?? "المملكة العربية السعودية");

      if (!resolvedDob) {
        setError("تم التحقق عبر يقين لكن تاريخ الميلاد غير متوفر. يرجى المحاولة مرة أخرى أو التواصل مع الدعم.");
        return;
      }

      setStep("form");
      setOtpSent(false);
      setInfo("تم التحقق بنجاح عبر يقين. أكمل بياناتك ثم أرسل رمز التحقق.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg || "فشل التحقق من خدمة يقين.");
    } finally {
      setYaqeenLoading(false);
      yaqeenRecaptchaRef.current?.reset();
      setYaqeenCaptcha(null);
    }
  };

  /* ------------------------------- STEP: Nafath (mandatory) ------------------------------- */
  const finalizeCreateAccount = useCallback(
    async (
      otp: string,
      nafath?: { nafathTransId?: string; nafathRequestId?: string }
    ) => {
    if (!user.name || !user.nationalId || !user.email || !user.phone || !user.dob) {
      throw new Error("يرجى تعبئة جميع الحقول المطلوبة.");
    }
    if (!challengeId) {
      throw new Error("رقم محاولة التحقق غير موجود. يرجى إعادة إرسال رمز التحقق.");
    }

    setCreateLoading(true);
    setStep("creating");
    try {
      await signUpUserPartner({
        user,
        otpCode: otp,
        challengeId,
        continueToken: continueTokenFromUrl || "",
        nafathTransId: nafath?.nafathTransId,
        nafathRequestId: nafath?.nafathRequestId,
      });

      try {
        localStorage.setItem(
          "residentPartnerProfile",
          JSON.stringify({
            login: user.login,
            name: user.name,
            nationalId: user.nationalId,
            phone: user.phone,
            email: user.email,
            countryName: user.countryName,
            dob: user.dob,
            dobHijri: dobHijri || undefined,
          })
        );
      } catch {
        /* ignore storage errors */
      }

      const from = (location.state as { from?: string })?.from;
      const redirectTo = returnTo || from || "/";
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "فشل إنشاء الحساب.";
      const msgLower = (msg || "").toLowerCase();
      const isExpired = msgLower.includes("expired");
      const isAlreadyExists =
        msgLower.includes("موجود") ||
        msgLower.includes("already exists") ||
        msgLower.includes("duplicate") ||
        msgLower.includes("already registered");

      if (isAlreadyExists) {
        setError("تم إنشاء الحساب مسبقاً. جاري توجيهك لتسجيل الدخول...");
        setInfo(null);
        setTimeout(() => navigate("/benefaction/login", { replace: true }), 2000);
        return;
      }

      setError(
        isExpired
          ? "انتهت صلاحية رمز التحقق. اضغط «إعادة إرسال رمز التحقق» أدناه ثم أدخل الرمز الجديد خلال دقائق."
          : msg
      );
      if (isExpired) setShowResendSection(true);

      // Allow user to go back to form to re-enter OTP / resend
      setStep("form");
      throw err instanceof Error ? err : new Error(msg);
    } finally {
      setCreateLoading(false);
    }
  }, [challengeId, continueTokenFromUrl, dobHijri, location.state, navigate, returnTo, user]);

  const handleNafathCompleted = useCallback(
    async ({ transId, requestId, identity }: NafathVerificationResult) => {
      const nin = onlyDigits(nationalId);
      if (onlyDigits(identity.nationalId || "") !== nin) {
        throw new Error("بيانات نفاذ لا تطابق رقم الهوية المدخل.");
      }
      setInfo("تم التحقق عبر نفاذ بنجاح. جاري إنشاء الحساب وتسجيل الدخول...");
      setError(null);
      const otp = pendingOtpCode.replace(/\D/g, "");
      if (!otp) throw new Error("رمز التحقق غير موجود. يرجى الرجوع وإدخاله.");
      await finalizeCreateAccount(otp, {
        nafathTransId: transId,
        nafathRequestId: requestId,
      });
    },
    [finalizeCreateAccount, nationalId, pendingOtpCode]
  );

  /* ------------------------------- STEP 2: Send OTP ------------------------------- */
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    // Validate password on React side before calling API.
    const passwordError = validatePassword(password, confirmPassword);
    if (passwordError) {
      setPasswordTouched(true);
      setConfirmPasswordTouched(true);
      setError(passwordError);
      return;
    }

    if (!isFormValid) {
      setError("يرجى تعبئة جميع الحقول المطلوبة ورقم هاتف صحيح (مثال: 05XXXXXXXX).");
      return;
    }
    if (!captchaToken) {
      setError("يرجى التحقق من reCAPTCHA.");
      return;
    }

    const { normalized } = normalizeKsaPhone(user.phone);
    if (!normalized) {
      setError("يرجى إدخال رقم هاتف صحيح.");
      return;
    }

    const emailTrim = user.email;
    if (!emailTrim || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      setError("يرجى إدخال بريد إلكتروني صحيح لإرسال رمز التحقق.");
      return;
    }

    setSendOtpLoading(true);
    try {
      // ✅ No mintContinue() here.
      // ✅ continue is optional; we only send it if it exists in URL.
      const otpRes = await signupInitiateOtp({
        nationalId: user.nationalId,
        mobileNumber: normalized,
        email: emailTrim,
      });

      setChallengeId(otpRes.challengeId);
      setOtpSent(true);
      setShowResendSection(false);
      setOtpCode("");
      // Start 60-second countdown before allowing resend.
      setResendSeconds(60);

      setInfo(
        otpRes.maskedMobile
          ? `يرجى إدخال رمز التحقق المرسل عبر الرسائل النصية إلى ${otpRes.maskedMobile}.`
          : "يرجى إدخال رمز التحقق المرسل عبر الرسائل النصية."
      );
    } catch (e: unknown) {
      
      const raw =
        (e as any)?.response?.data?.message ||
        (e as any)?.response?.data?.code ||
        (e instanceof Error ? e.message : String(e));

      const msg = (raw || "").toLowerCase();

      if (msg.includes("timeout")) setError("انتهت مهلة الاتصال. يرجى المحاولة مرة أخرى.");
      else if (msg.includes("invalid_phone")) setError("يرجى إدخال رقم هاتف صحيح.");
      else if (msg.includes("cooldown")) setError("يرجى الانتظار دقيقة قبل إعادة إرسال رمز التحقق.");
      else if (msg.includes("expired")) setError("انتهت صلاحية المحاولة السابقة. يرجى المحاولة مرة أخرى بعد دقيقة.");
      else if (msg.includes("rate_limited")) setError("تم تجاوز الحد المسموح. يرجى المحاولة لاحقاً.");
      else setError(raw || "فشل إرسال رمز التحقق.");
    } finally {
      setSendOtpLoading(false);
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  /* ------------------------------- STEP 2b: Resend OTP ------------------------------- */
  const handleResendOtp = async () => {
    setError(null);
    setInfo(null);

    if (resendSeconds > 0) {
      setError(`يرجى الانتظار ${resendSeconds} ثانية قبل إعادة إرسال رمز التحقق.`);
      return;
    }

    const { normalized } = normalizeKsaPhone(user.phone);
    const emailTrim = user.email;

    if (
      !normalized ||
      !emailTrim ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim) ||
      !resendCaptchaToken
    ) {
      setError("يرجى التحقق من reCAPTCHA وإدخال بريد إلكتروني صحيح ثم إعادة المحاولة.");
      return;
    }

    setResendOtpLoading(true);
    try {
      const otpRes = await signupInitiateOtp({
        nationalId: user.nationalId,
        mobileNumber: normalized,
        email: emailTrim,
      });

      setChallengeId(otpRes.challengeId);
      setOtpCode("");
      // Restart 60-second countdown after successful resend.
      setResendSeconds(60);

      setInfo(
        otpRes.maskedMobile
          ? `تم إرسال رمز جديد عبر الرسائل النصية إلى ${otpRes.maskedMobile}. أدخل الرمز الجديد ثم اضغط إنشاء الحساب.`
          : "تم إرسال رمز جديد عبر الرسائل النصية. أدخل الرمز الجديد ثم اضغط إنشاء الحساب."
      );
    } catch (e: unknown) {
      //const raw = e instanceof Error ? e.message : String(e);
      const raw =
        (e as any)?.response?.data?.message ||
        (e as any)?.response?.data?.code ||
        (e instanceof Error ? e.message : String(e));
        
      const msg = (raw || "").toLowerCase();

      if (msg.includes("timeout")) setError("انتهت مهلة الاتصال. يرجى المحاولة مرة أخرى.");
      else if (msg.includes("invalid_phone")) setError("يرجى إدخال رقم هاتف صحيح.");
      else if (msg.includes("cooldown")) setError("يرجى الانتظار دقيقة قبل إعادة إرسال رمز التحقق.");
      else if (msg.includes("expired")) setError("انتهت صلاحية المحاولة السابقة. يرجى المحاولة مرة أخرى بعد دقيقة.");
      else if (msg.includes("rate_limited")) setError("تم تجاوز الحد المسموح. يرجى المحاولة لاحقاً.");
      else setError(raw || "فشل إعادة الإرسال.");
    } finally {
      setResendOtpLoading(false);
      resendRecaptchaRef.current?.reset();
      setResendCaptchaToken(null);
    }
  };

  /* ------------------------------- STEP 3: Proceed to Nafath (after OTP entered) ------------------------------- */
  const handleProceedToNafath = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!user.name || !user.nationalId || !user.email || !user.phone || !user.dob) {
      setError("يرجى تعبئة جميع الحقول المطلوبة.");
      return;
    }

    // Re-check password before final account creation.
    const passwordError = validatePassword(password, confirmPassword);
    if (passwordError) {
      setPasswordTouched(true);
      setConfirmPasswordTouched(true);
      setError(passwordError);
      return;
    }

    const code = otpCode.replace(/\D/g, "");
    if (code.length < 1 || code.length > 6) {
      setError("رمز التحقق من 1 إلى 6 أرقام فقط.");
      return;
    }
    if (!challengeId) {
      setError("رقم محاولة التحقق غير موجود. يرجى إعادة إرسال رمز التحقق.");
      setShowResendSection(true);
      return;
    }

    // Store OTP for final creation after Nafath
    setPendingOtpCode(code);
    setInfo("تم إدخال رمز التحقق. أكمل التحقق عبر نفاذ للمتابعة.");
    setStep("nafath");
  };

  /* ------------------------------- UI ------------------------------- */
  const cardCls = "rounded-2xl border border-sa-200 bg-white shadow-sm ring-1 ring-black/5";
  const sectionTitleCls = "text-base font-semibold text-sa-700";
  const inputTallCls = `${inputBaseCls} h-11`;
  const selectTallCls = `${inputBaseCls} h-11`;

  const stepper = useMemo(() => {
    const items: Array<{ key: "identity" | "contact" | "nafath"; label: string }> = [
      { key: "identity", label: "التحقق من الهوية" },
      { key: "contact", label: "بيانات التواصل" },
      { key: "nafath", label: "نفاذ وإنشاء الحساب" },
    ];

    const idx =
      step === "yaqeen"
        ? 0
        : step === "form"
          ? 1
          : 2; // nafath/creating

    return { items, idx };
  }, [step]);

  const Stepper = () => {
    const s1 = stepper.items[0]; // logical step 1
    const s2 = stepper.items[1]; // logical step 2
    const s3 = stepper.items[2]; // logical step 3

    const circleCls = (logicalIdx: number) => {
      const done = logicalIdx < stepper.idx;
      const active = logicalIdx === stepper.idx;
      return [
        "h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold",
        done ? "bg-emerald-600 text-white" : active ? "bg-sa-600 text-white" : "bg-gray-200 text-gray-600",
      ].join(" ");
    };

    const lineCls = (done: boolean) => ["h-1 w-full rounded", done ? "bg-emerald-300" : "bg-gray-200"].join(" ");

    const labelCls2 = (logicalIdx: number) =>
      ["mt-2 text-[11px] text-center leading-snug", logicalIdx === stepper.idx ? "text-sa-800 font-semibold" : "text-gray-500"].join(
        " "
      );

    return (
      <div className="w-full max-w-2xl mx-auto mb-6" dir="rtl">
        <div className="grid grid-cols-[auto_1fr_auto_1fr_auto] items-center">
          {/* Left (1) */}
          <div className="flex flex-col items-center">
            <div className={circleCls(0)}>1</div>
            <div className={labelCls2(0)}>{s1.label}</div>
          </div>

          {/* Line between 1 and 2 */}
          <div className={lineCls(stepper.idx >= 1)} />

          {/* Middle (2) */}
          <div className="flex flex-col items-center">
            <div className={circleCls(1)}>2</div>
            <div className={labelCls2(1)}>{s2.label}</div>
          </div>

          {/* Line between 2 and 3 */}
          <div className={lineCls(stepper.idx >= 2)} />

          {/* Right (3) */}
          <div className="flex flex-col items-center">
            <div className={circleCls(2)}>3</div>
            <div className={labelCls2(2)}>{s3.label}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <main dir="rtl" className="min-h-screen bg-gray-100 py-12">
      <div className="dga-container">
        <div className="mx-auto w-full max-w-3xl">
          <div className={`${cardCls} min-h-[70vh]`}>
            <div className="p-6 lg:p-8">
              <header className="mb-6 flex flex-col items-center gap-2">
                <h1 className="text-2xl font-bold leading-tight text-sa-900 text-center">
                  إنشاء حساب جديد
                </h1>
              </header>

              <Stepper />

              <div className="mt-6">
            {/* STEP 1 — يقين (يشمل التحقق من وجود الحساب أولاً) */}

            {/* STEP 1 — يقين */}
            {step === "yaqeen" && (
              <form onSubmit={handleVerifyYaqeen}>
            <div className="mb-4 text-center">
              <h2 className={sectionTitleCls}>التحقق من خلال خدمة يقين</h2>
            </div>

            <div className="mb-4">
              <label className={labelCls}>رقم الهوية الوطنية</label>
              <input
                className={`${inputTallCls} text-right`}
                inputMode="numeric"
                pattern="\d*"
                value={nationalId}
                onChange={(e) => setNationalId(onlyDigits(e.target.value).slice(0, 20))}
                aria-label="رقم الهوية الوطنية"
                dir="rtl"
              />
            </div>

            {(() => {
              const digits = onlyDigits(nationalId);
              const showCitizenUi = !digits || isCitizenId(digits); // default to citizen UI before typing
              return showCitizenUi;
            })() ? (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>سنة الميلاد (هجري)</label>
                    <select className={selectTallCls} value={hijriY} onChange={(e) => setHijriY(e.target.value)}>
                      <option value="">اختر السنة</option>
                      {hijriYears.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>شهر الميلاد (هجري)</label>
                    <select className={selectTallCls} value={hijriM} onChange={(e) => setHijriM(e.target.value)}>
                      <option value="">اختر الشهر</option>
                      {HIJRI_MONTHS.map(([val, lbl]) => (
                        <option key={val} value={val}>
                          {`${val} - ${lbl}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className={helpCls}>سيتم إرسال السنة والشهر فقط (هجري)</p>
              </>
            ) : (
              <>
                <div className="mb-2">
                  <label className={labelCls}>تاريخ الميلاد (ميلادي)</label>
                  <input
                    type="date"
                    className={inputTallCls}
                    value={gregDate}
                    onChange={(e) => setGregDate(e.target.value)}
                    aria-label="تاريخ الميلاد"
                  />
                </div>
                <p className={helpCls}>سيتم إرسال التاريخ الكامل (يوم/شهر/سنة)</p>
              </>
            )}

            {!isCitizenId(nationalId) && !isResidentId(nationalId) && onlyDigits(nationalId).trim() && (
              <p className="mt-2 text-amber-700 text-sm">
                رقم الهوية: مواطن (يبدأ بـ 1) أو مقيم (يبدأ بـ 2 أو 3).
              </p>
            )}

            <div className="mt-4 flex justify-center">
              {RECAPTCHA_SITE_KEY ? (
                <ReCAPTCHA
                  ref={yaqeenRecaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(t) => setYaqeenCaptcha(t || null)}
                  onExpired={() => setYaqeenCaptcha(null)}
                  onErrored={() => setError("حدث خطأ في reCAPTCHA. يرجى تحديث الصفحة")}
                  hl="ar"
                />
              ) : (
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  مفاتيح reCAPTCHA غير متوفرة
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
                {error}
              </div>
            )}

            <div className="mt-5 flex justify-end">
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-xl bg-sa-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 disabled:opacity-50"
                disabled={yaqeenLoading || !yaqeenCaptcha || !nationalId.trim()}
              >
                {yaqeenLoading ? "جاري التحقق..." : "تحقق"}
              </button>
            </div>
              </form>
            )}

            {/* STEP 3 — نفاذ (إجباري) */}
            {step === "nafath" && (
              <>
                <NafathVerificationStep
                  nationalId={nationalId}
                  autoStart
                  title="التحقق عبر نفاذ (إجباري)"
                  subtitle="ستظهر لك أرقام التحقق لإكمال الموافقة في تطبيق نفاذ."
                  disabled={createLoading}
                  onBack={() => {
                    setStep("form");
                    setError(null);
                    setInfo(null);
                  }}
                  onCompleted={handleNafathCompleted}
                />
                {info && !error && (
                  <div className="mt-4 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                    {info}
                  </div>
                )}
                {error && (
                  <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
                    {error}
                  </div>
                )}
              </>
            )}

            {/* STEP 2 — معلومات الاتصال + OTP */}
            {step === "form" && (
              <form onSubmit={(e) => (otpSent ? handleProceedToNafath(e) : handleSendOtp(e))}>
            <div className="mb-4">
              <h2 className={sectionTitleCls}>معلومات الاتصال</h2>
            </div>

            <div className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              <div className="font-semibold">تم التحقق كـ: {name || "-"}</div>
              <div>
                تاريخ الميلاد (ميلادي): <strong dir="ltr">{dob || "—"}</strong>
              </div>
              {dobHijri ? (
                <div>
                  تاريخ الميلاد (هجري): <strong dir="ltr">{dobHijri}</strong>
                </div>
              ) : null}
            </div>

            <p className="mb-3 text-xs text-gray-500">البيانات التالية من خدمة يقين ولا يمكن تعديلها.</p>

            <div className="mb-4">
              <label className={labelCls}>الاسم الكامل</label>
              <input
                type="text"
                className={inputReadOnlyCls}
                placeholder="الاسم الكامل"
                value={name}
                readOnly
                dir="rtl"
                aria-readonly="true"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className={labelCls}>رقم الهوية الوطنية</label>
                <input
                  type="text"
                  className={inputReadOnlyCls}
                  placeholder="1XXXXXXXXX"
                  value={nationalId}
                  readOnly
                  dir="ltr"
                  aria-readonly="true"
                />
              </div>
              <div>
                <label className={labelCls}>رقم الهاتف</label>
              <input
                type="tel"
                className={inputTallCls}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="05XXXXXXXX أو 9665XXXXXXXX"
                  dir="ltr"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className={labelCls}>البريد الإلكتروني</label>
              <input
                type="email"
                className={inputTallCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                dir="ltr"
              />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className={labelCls}>كلمة المرور</label>
                <input
                  type={showPassword ? "text" : "password"}
                  className={passwordInputCls}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setPasswordTouched(true)}
                  placeholder="أدخل كلمة المرور"
                  dir="ltr"
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label className={labelCls}>تأكيد كلمة المرور</label>
                <input
                  type={showPassword ? "text" : "password"}
                  className={confirmPasswordInputCls}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={() => setConfirmPasswordTouched(true)}
                  placeholder="أعد إدخال كلمة المرور"
                  dir="ltr"
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="mt-2">
              <label className="inline-flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                />
                إظهار كلمة المرور
              </label>
            </div>

            <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
              <div className="mb-2 font-semibold text-sa-700">شروط كلمة المرور:</div>
              <ul className="grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
                <li className={passwordRules.minLength ? "text-emerald-700" : "text-red-700"}>
                  {passwordRules.minLength ? "✓" : "✕"} 8 أحرف أو أكثر
                </li>
                <li className={passwordRules.lower ? "text-emerald-700" : "text-red-700"}>
                  {passwordRules.lower ? "✓" : "✕"} حرف صغير واحد على الأقل
                </li>
                <li className={passwordRules.upper ? "text-emerald-700" : "text-red-700"}>
                  {passwordRules.upper ? "✓" : "✕"} حرف كبير واحد على الأقل
                </li>
                <li className={passwordRules.number ? "text-emerald-700" : "text-red-700"}>
                  {passwordRules.number ? "✓" : "✕"} رقم واحد على الأقل
                </li>
                <li className={passwordRules.special ? "text-emerald-700" : "text-red-700"}>
                  {passwordRules.special ? "✓" : "✕"} رمز خاص واحد على الأقل مثل @ أو #
                </li>
                <li className={isPasswordMatch ? "text-emerald-700" : "text-red-700"}>
                  {isPasswordMatch ? "✓" : "✕"} كلمة المرور وتأكيدها متطابقان
                </li>
              </ul>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className={labelCls}>الدولة</label>
                <input
                  type="text"
                  className={inputReadOnlyCls}
                  value={countryName}
                  readOnly
                  placeholder="من يقين"
                  dir="rtl"
                  aria-readonly="true"
                />
              </div>
              <div>
                <label className={labelCls}>تاريخ الميلاد (ميلادي)</label>
                <input
                  type="text"
                  className={inputReadOnlyCls}
                  value={dob || "—"}
                  readOnly
                  dir="ltr"
                  aria-readonly="true"
                />
              </div>
            </div>

            {dobHijri ? (
              <div className="mt-4">
                <label className={labelCls}>تاريخ الميلاد (هجري)</label>
                <input
                  type="text"
                  className={inputReadOnlyCls}
                  value={dobHijri}
                  readOnly
                  dir="ltr"
                  aria-readonly="true"
                />
              </div>
            ) : null}

            <div className="mt-4">
              <label className={labelCls}>اللغة</label>
              <select className={selectTallCls} value={userLang} onChange={(e) => setUserLang(e.target.value)}>
                <option value="ar">العربية</option>
                <option value="en">English</option>
              </select>
            </div>

            {/* SEND OTP */}
            {!otpSent && (
              <>
                <div className="mt-4 flex justify-center">
                  {RECAPTCHA_SITE_KEY ? (
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={(t) => setCaptchaToken(t || null)}
                      onExpired={() => setCaptchaToken(null)}
                      onErrored={() => setError("حدث خطأ في reCAPTCHA. يرجى تحديث الصفحة")}
                      hl="ar"
                    />
                  ) : (
                    <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      مفاتيح reCAPTCHA غير متوفرة
                    </div>
                  )}
                </div>

                {error && (
                  <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
                    {error}
                  </div>
                )}

                {!isFormValid && (
                  <p className="mt-3 text-xs text-amber-800">
                    {!dob
                      ? "تاريخ الميلاد من يقين غير متوفر — ارجع لخطوة التحقق وأعد المحاولة."
                      : "أكمل الجوال والبريد وكلمة المرور بشكل صحيح، ثم أكمل reCAPTCHA."}
                  </p>
                )}

                <div className="mt-5 flex justify-end">
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center rounded-xl bg-sa-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 disabled:opacity-50"
                    disabled={!isFormValid || !captchaToken || sendOtpLoading}
                  >
                    {sendOtpLoading ? "جاري الإرسال..." : "إرسال رمز التحقق"}
                  </button>
                </div>
              </>
            )}

            {/* OTP SENT + CREATE */}
            {otpSent && (
              <>
                {info && (
                  <div className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                    {info}
                  </div>
                )}

                <div className="mt-4">
                  <label className={labelCls}>أدخل رمز التحقق</label>
                  <input
                    className={inputBaseCls}
                    placeholder="••••••"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !otpCode.trim()) e.preventDefault();
                    }}
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={6}
                    dir="ltr"
                  />
                  <p className={helpCls}>أدخل رمز التحقق المرسل عبر الرسائل النصية (من 1 إلى 6 أرقام)</p>
                </div>

                {!showResendSection ? (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => setShowResendSection(true)}
                      disabled={resendSeconds > 0}
                      className="text-sm text-sa-600 hover:text-sa-700 underline disabled:cursor-not-allowed disabled:text-gray-400"
                    >
                      {resendSeconds > 0
                        ? `إعادة إرسال رمز التحقق بعد ${resendSeconds} ثانية`
                        : "لم تصلك الرسالة؟ إعادة إرسال رمز التحقق"}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mt-4 flex justify-center">
                      {RECAPTCHA_SITE_KEY ? (
                        <ReCAPTCHA
                          ref={resendRecaptchaRef}
                          sitekey={RECAPTCHA_SITE_KEY}
                          onChange={(t) => setResendCaptchaToken(t || null)}
                          onExpired={() => setResendCaptchaToken(null)}
                          onErrored={() => setError("حدث خطأ في reCAPTCHA. يرجى تحديث الصفحة")}
                          hl="ar"
                        />
                      ) : null}
                    </div>
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={resendOtpLoading || !resendCaptchaToken || resendSeconds > 0}
                        className="inline-flex items-center justify-center w-full rounded-xl border border-sa-300 bg-white px-4 py-2 text-sm font-semibold text-sa-700 shadow-sm hover:bg-sa-50 focus:outline-none focus:ring-2 focus:ring-sa-500 disabled:opacity-50"
                      >
                        {resendOtpLoading
                          ? "جاري الإرسال..."
                          : resendSeconds > 0
                            ? `إعادة الإرسال بعد ${resendSeconds} ثانية`
                            : "إعادة إرسال رمز التحقق"}
                      </button>
                    </div>
                  </>
                )}

                {error && (
                  <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
                    {error}
                  </div>
                )}

                <div className="mt-5 flex justify-end">
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50"
                    disabled={createLoading}
                  >
                    {createLoading ? "جاري إنشاء الحساب..." : "المتابعة إلى نفاذ"}
                  </button>
                </div>
              </>
            )}
              </form>
            )}

            {step === "creating" && (
              <section className="text-center">
            <h2 className={sectionTitleCls}>جاري إنشاء الحساب...</h2>
            <p className="text-sm text-gray-600 mt-2">يرجى الانتظار</p>
            {error && (
              <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
                {error}
              </div>
            )}
              </section>
            )}
          </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}